import { type Control } from 'react-hook-form';
import {
  Stack,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ButtonBase,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ApartmentIcon from '@mui/icons-material/Apartment';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';

import { useDialog } from '#packages/web/hooks/use-dialog';

import { AddressDetails } from './details/address-details';
import { SiteContactsDetails } from './details/site-contacts-details';
import { SiteVisitDetails } from './details/site-visit-date-details';
import { WeatherConditionDetails } from './details/weather-condition-details';
import { IdentificationDetails } from './details/identification-details';
import { FloorsDetails } from './details/floors-details';
import { MurbSuitesDetails } from './details/murb-suites-details';
import { OccupantsDetails } from './details/occupants-details';
import { MetersDetails } from './details/meters-details';
import { OtherDetails } from './details/other-details';

import { IdentificationFormDialog } from './dialogs/identification-form-dialog';
import { AddressFormDialog } from './dialogs/address-form-dialog';
import { SiteVisitDateFormDialog } from './dialogs/site-visit-date-form-dialog';
import { WeatherFormDialog } from './dialogs/weather-form-dialog';
import { OtherFormDialog } from './dialogs/other-form-dialog';
import { MetersFormDialog } from './dialogs/meters-form-dialog';
import { FloorsFormDialog } from './dialogs/floors-form-dialog';
import { OccupantsFormDialog } from './dialogs/occupants-form-dialog';
import { MurbSuitesFormDialog } from './dialogs/murb-suites-form-dialog';
import { SiteContactsFormDialog } from './dialogs/site-contacts-form-dialog';

export type GeneralFieldsProps = {
  control: Control<EditAuditFormBackgroundData>;
  disableForm: boolean;
};

type SubformType =
  | 'identification'
  | 'address'
  | 'site_contacts'
  | 'site_visit_dates'
  | 'weather'
  | 'floors'
  | 'murb_suites'
  | 'occupants'
  | 'meters'
  | 'other';

export const GeneralFields = ({ control, disableForm }: GeneralFieldsProps) => {
  const subFormDialog = useDialog<SubformType>();

  return (
    <>
      <Accordion
        variant="outlined"
        defaultExpanded
        sx={(theme) => ({
          borderColor: control.getFieldState('general').error
            ? theme.palette.error.main
            : undefined,
        })}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header">
          <Stack direction="row" alignItems="center" spacing={1}>
            <ApartmentIcon fontSize="small" />
            <Typography variant="body1">General Information</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('identification')}>
              <IdentificationDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('address')}>
              <AddressDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('site_contacts')}>
              <SiteContactsDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('site_visit_dates')}>
              <SiteVisitDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('weather')}>
              <WeatherConditionDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('floors')}>
              <FloorsDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('murb_suites')}>
              <MurbSuitesDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('occupants')}>
              <OccupantsDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('meters')}>
              <MetersDetails control={control} />
            </ButtonBase>
            <ButtonBase
              disabled={disableForm}
              onClick={() => subFormDialog.open('other')}>
              <OtherDetails control={control} />
            </ButtonBase>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <IdentificationFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'identification'}
      />
      <AddressFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'address'}
      />
      <SiteContactsFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'site_contacts'}
      />
      <SiteVisitDateFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'site_visit_dates'}
      />
      <WeatherFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'weather'}
      />
      <FloorsFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'floors'}
      />
      <MurbSuitesFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'murb_suites'}
      />
      <OccupantsFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'occupants'}
      />
      <MetersFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'meters'}
      />
      <OtherFormDialog
        control={control}
        onClose={() => subFormDialog.close()}
        open={subFormDialog.data === 'other'}
      />
    </>
  );
};
